import axios from 'axios';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-logout on 401 — but NOT on the login endpoint itself
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLoginEndpoint = error.config?.url?.includes('/auth/login');
    if (error.response?.status === 401 && !isLoginEndpoint) {
      const userType = localStorage.getItem('user_type');
      localStorage.removeItem('token');
      localStorage.removeItem('user_type');
      localStorage.removeItem('user_name');
      localStorage.removeItem('user_tier');
      window.location.href = userType === 'RECRUITER' ? '/recruiter/login' : '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', new URLSearchParams(data), {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
  }),
};

export const jobsAPI = {
  getJobs: (params) => api.get('/jobs/', { params }),           // trailing slash — avoids 307
  getJob: (id) => api.get(`/jobs/${id}`),
  getJobsByRecruiter: (userId) => api.get(`/jobs/by-recruiter/${userId}`),
};

export const cvAPI = {
  uploadCV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/cv/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getMyCV: () => api.get('/cv/my-cv'),
  deleteMyCV: () => api.delete('/cv/my-cv'),
  reparseCV: () => api.post('/cv/reparse'),
};

export const matchingAPI = {
  matchJobs: (params) => api.post('/matching/match', null, { params }),
  getMyMatches: (params) => api.get('/matching/matches', { params }),
  getJobMatchScore: (jobId) => api.get(`/matching/match/${jobId}`),
};

export const autoApplyAPI = {
  getSettings: () => api.get('/auto-apply/settings'),
  updateSettings: (data) => api.post('/auto-apply/settings', data),
  patchSettings: (data) => api.patch('/auto-apply/settings', data),
  getStats: () => api.get('/auto-apply/stats'),
  toggle: () => api.post('/auto-apply/toggle'),
  runNow: () => api.post('/auto-apply/run'),
};

export const recruiterAPI = {
  createJob: (data) => api.post('/jobs/', data),
  getMyJobs: () => api.get('/jobs/my-jobs/'),
  updateJob: (jobId, data) => api.put(`/jobs/${jobId}`, data),
  deleteJob: (jobId) => api.delete(`/jobs/${jobId}`),
  getProfile: () => api.get('/recruiter-profile/me/'),
  updateProfile: (data) => api.put('/recruiter-profile/me/', data),
  createProfile: (data) => api.post('/recruiter-profile/', data),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/recruiter-profile/me/logo/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

export const companyAPI = {
  getByCompany: (companyName) => api.get('/recruiter-profile/by-company/', { params: { name: companyName } }),
};

export const searchAPI = {
  searchCompanies: (params) => api.get('/search/companies', { params }),
  searchSeekers: (params) => api.get('/search/seekers', { params }),
};

/**
 * Fetch the current user's subscription tier from the backend and sync it
 * to localStorage so all pages reflect the live plan immediately.
 */
export const refreshTier = async () => {
  const token = localStorage.getItem('token');
  if (!token) return;
  try {
    const res = await api.get('/payments/tiers/');
    if (res.data?.current_tier) {
      localStorage.setItem('user_tier', res.data.current_tier);
    }
  } catch {}
};

export default api;
